import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2, FileText, Table as TableIcon, Printer, IndianRupee, Coins, ChevronsUpDown, Search, X, Users, CheckCircle2, Settings2, UserCog, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { syncPayroll, getPayrollSyncStatus, getPayrollLastSynced, getPayrollMatrix, getPayrollStaff, getPayslip, getPayslips, getPayrollRates, savePayrollRate, exportPayslipsCsv } from '@/services/payrollApi';
import PayrollSettingsTab from './PayrollSettingsTab';
import SupervisorConfig from './SupervisorConfig';
import SupervisorAttendance from './SupervisorAttendance';
import SupervisorPayslip from './SupervisorPayslip';

const money = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyFmt = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const todayStr = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD (local)

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const Payslip = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('matrix');
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const pollRef = useRef(null);

  const [staffList, setStaffList] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [slips, setSlips] = useState([]);
  const [slipLoading, setSlipLoading] = useState(false);
  const [showBreakup, setShowBreakup] = useState(false);

  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateAdmin, setRateAdmin] = useState(false);
  const [rateEdits, setRateEdits] = useState({});
  const [rateTargetEdits, setRateTargetEdits] = useState({});
  const [savingRate, setSavingRate] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const blob = await exportPayslipsCsv({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslips_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Payslip CSV downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPayrollMatrix({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
      setMatrix(data);
    } catch (e) {
      toast.error('Failed to load payroll matrix');
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  const loadStaff = useCallback(async () => {
    try { setStaffList((await getPayrollStaff()).staff || []); } catch (e) { /* noop */ }
  }, []);

  const loadLastSynced = useCallback(async () => {
    try { setLastSynced(await getPayrollLastSynced()); } catch (e) { /* noop */ }
  }, []);

  const loadRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      const data = await getPayrollRates();
      setRates(data.rates || []);
      setRateAdmin(!!data.is_admin);
      const edits = {};
      const tEdits = {};
      (data.rates || []).forEach((r) => {
        edits[r.operation_code] = String(r.rate ?? 0);
        tEdits[r.operation_code] = String(r.target_qty ?? 0);
      });
      setRateEdits(edits);
      setRateTargetEdits(tEdits);
    } catch (e) { toast.error('Failed to load rates'); }
    finally { setRatesLoading(false); }
  }, []);

  const pollSyncStatus = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const st = await getPayrollSyncStatus(jobId);
        setSyncJob(st);
        if (st.status === 'completed' || st.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncing(false);
          if (st.status === 'completed') {
            toast.success(`Posted ${st.entries_posted?.toLocaleString('en-IN') || 0} payroll entries`);
            await loadMatrix();
            await loadStaff();
            await loadLastSynced();
          } else {
            toast.error(`Backfill failed: ${st.error || 'unknown error'}`);
          }
          setTimeout(() => setSyncJob(null), 5000);
        }
      } catch (e) { /* transient — keep polling */ }
    }, 2000);
  }, [loadMatrix, loadStaff, loadLastSynced]);

  useEffect(() => {
    loadMatrix(); loadStaff(); loadRates(); loadLastSynced();
    // Resume tracking any backfill already running (e.g. after a page refresh).
    (async () => {
      try {
        const st = await getPayrollSyncStatus();
        if (st && st.status === 'running' && st.job_id) {
          setSyncing(true);
          setSyncJob(st);
          pollSyncStatus(st.job_id);
        }
      } catch (e) { /* noop */ }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  const handleSaveRate = async (op) => {
    const val = parseFloat(rateEdits[op.operation_code]);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid rate'); return; }
    const tRaw = rateTargetEdits[op.operation_code];
    const tVal = tRaw === '' || tRaw === undefined ? 0 : parseFloat(tRaw);
    if (isNaN(tVal) || tVal < 0) { toast.error('Enter a valid target qty'); return; }
    setSavingRate(op.operation_code);
    try {
      await savePayrollRate({ operationCode: op.operation_code, rate: val, unitType: op.unit_type, targetQty: tVal });
      toast.success(`Saved rate & target for ${op.operation}`);
      await loadRates();
      await loadMatrix();
      if (selectedStaffIds.length) await loadPayslips(selectedStaffIds);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingRate(null); }
  };

  const handleSync = async (scope) => {
    setSyncing(true);
    try {
      const res = await syncPayroll(scope === 'range'
        ? { scope: 'range', dateFrom, dateTo }
        : { scope: 'all' });
      if (res.mode === 'async' && res.job_id) {
        setSyncJob({
          status: 'running', operations_done: 0,
          operations_total: res.operations_total || 0, current_operation: null,
        });
        toast.info(res.already_running
          ? 'A backfill is already running — tracking its progress.'
          : 'Backfill started in the background — you can keep working.');
        pollSyncStatus(res.job_id);
      } else {
        toast.success(`Posted ${res.entries_posted} payroll entries`);
        await loadMatrix();
        await loadStaff();
        setSyncing(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  };

  const loadPayslips = async (ids, detailOverride) => {
    const list = ids ?? selectedStaffIds;
    if (!list || list.length === 0) { setSlips([]); return; }
    const detail = detailOverride ?? showBreakup;
    setSlipLoading(true);
    try {
      const data = await getPayslips({ staffIds: list, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, detail });
      setSlips(data.payslips || []);
    } catch (e) {
      toast.error('Failed to load payslips');
    } finally { setSlipLoading(false); }
  };

  const handleToggleBreakup = (v) => {
    setShowBreakup(v);
    if (selectedStaffIds.length) loadPayslips(selectedStaffIds, v);
  };

  const filteredStaff = staffList.filter((s) => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return true;
    return (s.staff_name || '').toLowerCase().includes(q) || (s.staff_id || '').toLowerCase().includes(q);
  });

  const toggleStaff = (id) => {
    const next = selectedStaffIds.includes(id)
      ? selectedStaffIds.filter((x) => x !== id)
      : [...selectedStaffIds, id];
    setSelectedStaffIds(next);
    loadPayslips(next);
  };

  const selectAllFiltered = () => {
    const next = Array.from(new Set([...selectedStaffIds, ...filteredStaff.map((s) => s.staff_id)]));
    setSelectedStaffIds(next);
    loadPayslips(next);
  };

  const clearAllStaff = () => { setSelectedStaffIds([]); setSlips([]); };

  const staffName = (id) => (staffList.find((s) => s.staff_id === id)?.staff_name) || id;
  const combinedTotal = slips.reduce((sum, s) => sum + (s.total_amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg print:hidden">
        <div className="max-w-[1800px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/accounts')}
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <IndianRupee className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Payslip</h1>
                <p className="text-xs text-[#E8DCC8]">Payroll entries — qty, rate & amount per staff / operation</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastSynced?.last_synced_at && (
              <div className="hidden md:flex items-center gap-1.5 text-xs text-[#E8DCC8]"
                data-testid="last-synced-badge"
                title={`Last full sync: ${new Date(lastSynced.last_synced_at).toLocaleString('en-IN')}${lastSynced.entries_posted ? ` · ${lastSynced.entries_posted.toLocaleString('en-IN')} entries` : ''}`}>
                <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
                <span>
                  Payroll last synced <span className="font-medium text-white">{timeAgo(lastSynced.last_synced_at)}</span>
                  <span className="opacity-70"> · {lastSynced.triggered_by === 'scheduler' ? 'nightly' : 'manual'}</span>
                </span>
              </div>
            )}
            <Button onClick={() => handleSync('all')} variant="secondary" size="sm" disabled={syncing} data-testid="sync-all-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Post All (backfill)
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 space-y-5">
        {/* Background backfill progress */}
        {syncJob && syncJob.status === 'running' && (
          <Card className="print:hidden border-[#2D5F4F]/40 bg-[#F0F7F4]" data-testid="sync-progress-banner">
            <CardContent className="py-3 flex items-center gap-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#2D5F4F] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1A1F2E]" data-testid="sync-progress-text">
                  Backfill running… {syncJob.operations_done || 0}/{syncJob.operations_total || 0} operations
                  {syncJob.current_operation ? ` · ${syncJob.current_operation}` : ''}
                </div>
                <div className="mt-1 h-2 w-full bg-[#D9E5DF] rounded-full overflow-hidden">
                  <div className="h-full bg-[#2D5F4F] transition-[width] duration-500"
                    style={{ width: `${syncJob.operations_total ? Math.round((syncJob.operations_done / syncJob.operations_total) * 100) : 0}%` }} />
                </div>
              </div>
              <span className="text-xs text-[#5A5F6E] hidden sm:block shrink-0">Safe to leave — it keeps running.</span>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="print:hidden">
          <CardContent className="py-4 flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs">From (work date)</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="date-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="date-to" />
            </div>
            <Button onClick={() => { loadMatrix(); if (selectedStaffIds.length) loadPayslips(selectedStaffIds); }} data-testid="apply-filter-btn" className="bg-[#2D5F4F] hover:bg-[#234A3D]">
              Apply
            </Button>
            <Button variant="outline" onClick={() => handleSync('range')} disabled={syncing || !dateFrom || !dateTo} data-testid="sync-range-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Post this range
            </Button>
            <Button variant="outline" onClick={handleExportCsv} disabled={exporting} data-testid="export-csv-btn"
              className="border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F]/10">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV (all staff)
            </Button>
            <div className="ml-auto flex gap-2">
              <Button variant={tab === 'matrix' ? 'default' : 'outline'} onClick={() => setTab('matrix')} data-testid="tab-matrix">
                <TableIcon className="w-4 h-4 mr-2" />Matrix
              </Button>
              <Button variant={tab === 'payslip' ? 'default' : 'outline'} onClick={() => setTab('payslip')} data-testid="tab-payslip">
                <FileText className="w-4 h-4 mr-2" />Payslip
              </Button>
              <Button variant={tab === 'rates' ? 'default' : 'outline'} onClick={() => setTab('rates')} data-testid="tab-rates">
                <Coins className="w-4 h-4 mr-2" />Rates
              </Button>
              <Button variant={tab === 'supervisors' ? 'default' : 'outline'} onClick={() => setTab('supervisors')} data-testid="tab-supervisors">
                <UserCog className="w-4 h-4 mr-2" />Supervisors
              </Button>
              <Button variant={tab === 'settings' ? 'default' : 'outline'} onClick={() => setTab('settings')} data-testid="tab-settings">
                <Settings2 className="w-4 h-4 mr-2" />Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {tab === 'matrix' && (
          <Card>
            <CardHeader className="print:hidden">
              <CardTitle>Payroll Matrix</CardTitle>
              <CardDescription>
                Operations in rows, staff in columns. Each cell: Qty · @rate · amount.
                {matrix && <span className="ml-2 font-medium">Total: {money(matrix.grand_total?.amount)}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading...</div>
              ) : !matrix || matrix.operation_rows.length === 0 ? (
                <div className="py-12 text-center text-[#5A5F6E]">No payroll entries. Click "Post All (backfill)" to populate.</div>
              ) : (
                <div className="overflow-auto border rounded-lg max-h-[70vh]" data-testid="matrix-table-wrap">
                  <table className="text-xs border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#1A1F2E] text-white">
                        <th className="sticky left-0 z-20 bg-[#1A1F2E] px-3 py-2 text-left min-w-[130px]">Module</th>
                        <th className="sticky left-[130px] z-20 bg-[#1A1F2E] px-3 py-2 text-left min-w-[180px]">Operation</th>
                        {matrix.staff_columns.map((s) => (
                          <th key={s.staff_id} className="px-3 py-2 text-center min-w-[110px] border-l border-white/10" title={s.staff_id}>
                            {s.staff_name}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-center min-w-[110px] bg-[#2D5F4F] border-l border-white/10">Row Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t bg-[#F0F7F4] font-medium" data-testid="matrix-present-days-row">
                        <td className="sticky left-0 z-10 bg-[#F0F7F4] px-3 py-2 text-[#2D5F4F] border-r" colSpan={2}>Present Days</td>
                        {matrix.staff_columns.map((s) => (
                          <td key={s.staff_id} className="px-2 py-1.5 text-center border-l font-semibold text-[#1A1F2E]" data-testid={`matrix-present-days-${s.staff_id}`}>
                            {s.present_days ?? 0}
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center bg-[#F0F7F4] border-l text-gray-400">—</td>
                      </tr>
                      {matrix.operation_rows.map((row) => (
                        <tr key={row.operation_code} className="border-t hover:bg-[#F5F1E8]" data-testid={`matrix-row-${row.operation_code}`}>
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 text-[#5A5F6E] border-r" data-testid={`matrix-module-${row.operation_code}`}>{row.module || '—'}</td>
                          <td className="sticky left-[130px] z-10 bg-white px-3 py-2 font-medium border-r">{row.operation}</td>
                          {matrix.staff_columns.map((s) => {
                            const c = row.cells[s.staff_id];
                            return (
                              <td key={s.staff_id} className="px-2 py-1.5 text-center border-l align-top">
                                {c ? (
                                  <div className="leading-tight">
                                    <div className="text-[#5A5F6E]">{qtyFmt(c.qty)} @{c.rate}</div>
                                    <div className="font-semibold text-[#1A1F2E]">{money(c.amount)}</div>
                                  </div>
                                ) : <span className="text-gray-300">–</span>}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-center bg-[#F5F1E8] font-semibold border-l">{money(row.row_total.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 bg-[#2D5F4F] text-white sticky bottom-0">
                        <td className="sticky left-0 z-10 bg-[#2D5F4F] px-3 py-2 font-bold border-r border-white/10" colSpan={2}>Column Total</td>
                        {matrix.staff_columns.map((s) => (
                          <td key={s.staff_id} className="px-2 py-2 text-center font-semibold border-l border-white/10">
                            {money(matrix.column_totals[s.staff_id]?.amount)}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center font-bold border-l border-white/10">{money(matrix.grand_total?.amount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'payslip' && (
          <Card>
            <CardHeader className="print:hidden">
              <CardTitle>Staff Payslips</CardTitle>
              <CardDescription>Search and select one or more staff to view and print their payslips (one page per staff).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3 print:hidden">
                <div className="min-w-[300px]">
                  <Label className="text-xs">Staff</Label>
                  <Popover open={staffPickerOpen} onOpenChange={setStaffPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="mt-1 w-[300px] justify-between font-normal" data-testid="payslip-staff-select">
                        <span className="flex items-center gap-2 truncate">
                          <Users className="w-4 h-4 opacity-60 shrink-0" />
                          {selectedStaffIds.length === 0
                            ? <span className="text-[#5A5F6E]">Select staff</span>
                            : `${selectedStaffIds.length} staff selected`}
                        </span>
                        <ChevronsUpDown className="w-4 h-4 opacity-60 ml-2 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <div className="p-2 border-b space-y-2">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5F6E]" />
                          <Input value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)}
                            placeholder="Search name or ID..." className="pl-8 h-9" data-testid="staff-search-input" />
                        </div>
                        <div className="flex items-center justify-between">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAllFiltered} data-testid="staff-select-all">
                            Select all{staffSearch.trim() ? ' (filtered)' : ''}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearAllStaff} data-testid="staff-clear-all">
                            Clear
                          </Button>
                        </div>
                      </div>
                      <ScrollArea className="max-h-64">
                        <div className="p-1">
                          {filteredStaff.length === 0 && (
                            <div className="py-6 text-center text-xs text-[#5A5F6E]">No staff found.</div>
                          )}
                          {filteredStaff.map((s) => (
                            <button key={s.staff_id} type="button" onClick={() => toggleStaff(s.staff_id)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F1E8] text-left text-sm"
                              data-testid={`staff-option-${s.staff_id}`}>
                              <Checkbox checked={selectedStaffIds.includes(s.staff_id)} className="pointer-events-none" />
                              <span className="truncate">{s.staff_name} <span className="text-[#5A5F6E]">({s.staff_id})</span></span>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                </div>
                {slips.length > 0 && (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-[#1A1F2E] cursor-pointer print:hidden" data-testid="breakup-toggle-label">
                      <Switch checked={showBreakup} onCheckedChange={handleToggleBreakup} data-testid="breakup-toggle" />
                      Shift &amp; day-wise breakup
                    </label>
                    <Button onClick={() => window.print()} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="print-payslip-btn">
                      <Printer className="w-4 h-4 mr-2" />Print / PDF ({slips.length})
                    </Button>
                  </div>
                )}
              </div>

              {/* selected chips */}
              {selectedStaffIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 print:hidden" data-testid="selected-staff-chips">
                  {selectedStaffIds.map((id) => (
                    <Badge key={id} variant="secondary" className="gap-1 pr-1">
                      {staffName(id)}
                      <button type="button" onClick={() => toggleStaff(id)} className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
                        data-testid={`chip-remove-${id}`} aria-label={`Remove ${staffName(id)}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {slipLoading ? (
                <div className="flex items-center justify-center py-12 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading...</div>
              ) : slips.length > 0 ? (
                <div className="space-y-6" data-testid="payslip-body">
                  {slips.length > 1 && (
                    <div className="text-sm text-[#5A5F6E] print:hidden" data-testid="payslip-combined-total">
                      {slips.length} payslips · Combined total <span className="font-semibold text-[#2D5F4F]">{money(combinedTotal)}</span>
                    </div>
                  )}
                  {slips.map((s, si) => (
                    <div key={s.staff_id}
                      className={`border rounded-lg p-6 bg-white ${si > 0 ? 'print:break-before-page' : ''}`}
                      data-testid={`payslip-body-${s.staff_id}`}>
                      <div className="flex items-center justify-between border-b pb-4 mb-4">
                        <div>
                          <h2 className="text-2xl font-bold text-[#1A1F2E]">Work Summary</h2>
                          <p className="text-sm text-[#5A5F6E]">
                            {(dateFrom || s.filter?.date_from) || 'Beginning'} → {(dateTo || s.filter?.date_to) || 'To date'}
                          </p>
                          <p className="text-sm font-semibold text-[#2D5F4F] mt-1" data-testid={`payslip-present-days-${s.staff_id}`}>
                            Present Days: {s.present_days ?? 0}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-lg">{s.staff_name}</p>
                          <p className="text-sm text-[#5A5F6E]">{s.staff_id}</p>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-[#5A5F6E]">
                            <th className="py-2">Module</th>
                            <th className="py-2">Operation</th>
                            <th className="py-2">Unit</th>
                            <th className="py-2 text-right">Qty</th>
                            <th className="py-2 text-right">Rate</th>
                            <th className="py-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.lines.length === 0 && (
                            <tr><td colSpan={6} className="py-6 text-center text-[#5A5F6E]">No entries in this period.</td></tr>
                          )}
                          {s.lines.map((l, i) => {
                            const shiftRows = (l.by_shift || []).filter(Boolean);
                            const showShifts = showBreakup && shiftRows.length > 0 && (shiftRows.length > 1 || shiftRows[0].shift_id);
                            const dayRows = (l.by_day || []);
                            const showDays = showBreakup && dayRows.length > 1;
                            return (
                            <React.Fragment key={i}>
                              <tr className="border-b" data-testid={`payslip-line-${s.staff_id}-${i}`}>
                                <td className="py-2 text-[#5A5F6E]">{l.module || '—'}</td>
                                <td className="py-2">{l.operation}</td>
                                <td className="py-2 capitalize">{l.unit_type}</td>
                                <td className="py-2 text-right">{qtyFmt(l.qty)}</td>
                                <td className="py-2 text-right">{l.unit_rate}</td>
                                <td className="py-2 text-right font-medium">{money(l.amount)}</td>
                              </tr>
                              {(showShifts || showDays) && (
                                <tr className="border-b bg-[#F9F7F1]" data-testid={`payslip-breakup-${s.staff_id}-${i}`}>
                                  <td></td>
                                  <td colSpan={5} className="py-2">
                                    <div className="flex flex-wrap gap-x-10 gap-y-2 text-xs text-[#5A5F6E]">
                                      {showShifts && (
                                        <div>
                                          <div className="font-semibold text-[#1A1F2E] mb-0.5">By shift</div>
                                          {shiftRows.map((sh, si) => (
                                            <div key={si} className="flex gap-2">
                                              <span className="min-w-[110px]">{sh.shift_name}</span>
                                              <span className="tabular-nums">{qtyFmt(sh.qty)} pcs</span>
                                              <span className="tabular-nums font-medium text-[#2D5F4F]">{money(sh.amount)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {showDays && (
                                        <div>
                                          <div className="font-semibold text-[#1A1F2E] mb-0.5">Day-wise</div>
                                          {dayRows.map((d, di) => (
                                            <div key={di} className="flex gap-2">
                                              <span className="min-w-[110px]">{d.work_date}</span>
                                              <span className="tabular-nums">{qtyFmt(d.qty)} pcs</span>
                                              <span className="tabular-nums font-medium text-[#2D5F4F]">{money(d.amount)}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 font-bold">
                            <td className="py-3" colSpan={5}>Total</td>
                            <td className="py-3 text-right text-[#2D5F4F]" data-testid={`payslip-total-${s.staff_id}`}>{money(s.total_amount)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-[#5A5F6E]">Search and select staff to view their payslips.</div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'rates' && (
          <Card>
            <CardHeader className="print:hidden">
              <CardTitle>Operation Rates — Rate / USN & Target</CardTitle>
              <CardDescription>
                Set the pay rate per USN and the daily Target Qty/staff for each operation.
                Amount = Qty × Rate, applied live (no re-post). Shift premiums apply only to
                night-shift pieces <b>beyond</b> the target. Target 0 = no gating (premium on all night pieces).
                {!rateAdmin && <span className="ml-2 text-[#B45309]">Read-only — admin access required to edit.</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ratesLoading ? (
                <div className="flex items-center justify-center py-12 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading...</div>
              ) : (
                <div className="overflow-auto border rounded-lg" data-testid="rates-table-wrap">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#1A1F2E] text-white text-left">
                        <th className="px-4 py-2 min-w-[140px]">Module</th>
                        <th className="px-4 py-2 min-w-[200px]">Operation</th>
                        <th className="px-4 py-2 text-right min-w-[150px]">Rate / USN (₹)</th>
                        <th className="px-4 py-2 text-right min-w-[150px]">Target Qty / day</th>
                        {rateAdmin && <th className="px-4 py-2 text-center min-w-[100px]">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rates.length === 0 && (
                        <tr><td colSpan={rateAdmin ? 5 : 4} className="py-6 text-center text-[#5A5F6E]">No operations defined.</td></tr>
                      )}
                      {rates.map((r) => (
                        <tr key={r.operation_code} className="border-t hover:bg-[#F5F1E8]" data-testid={`rate-row-${r.operation_code}`}>
                          <td className="px-4 py-2 text-[#5A5F6E]">{r.module}</td>
                          <td className="px-4 py-2 font-medium">{r.operation}</td>
                          <td className="px-4 py-2 text-right">
                            {rateAdmin ? (
                              <Input type="number" step="0.01" min="0"
                                value={rateEdits[r.operation_code] ?? ''}
                                onChange={(e) => setRateEdits({ ...rateEdits, [r.operation_code]: e.target.value })}
                                className="w-28 ml-auto text-right"
                                data-testid={`rate-input-${r.operation_code}`} />
                            ) : (
                              <span className="font-medium">{money(r.rate)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {rateAdmin ? (
                              <Input type="number" step="1" min="0"
                                value={rateTargetEdits[r.operation_code] ?? ''}
                                onChange={(e) => setRateTargetEdits({ ...rateTargetEdits, [r.operation_code]: e.target.value })}
                                className="w-28 ml-auto text-right"
                                placeholder="0"
                                data-testid={`target-input-${r.operation_code}`} />
                            ) : (
                              <span className="font-medium">{qtyFmt(r.target_qty) || 0}</span>
                            )}
                          </td>
                          {rateAdmin && (
                            <td className="px-4 py-2 text-center">
                              <Button size="sm" className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                                disabled={savingRate === r.operation_code}
                                onClick={() => handleSaveRate(r)}
                                data-testid={`rate-save-${r.operation_code}`}>
                                {savingRate === r.operation_code ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'supervisors' && (
          <div className="space-y-5">
            <SupervisorAttendance dateFrom={dateFrom} dateTo={dateTo} />
            <SupervisorPayslip dateFrom={dateFrom} dateTo={dateTo} money={money} qtyFmt={qtyFmt} />
          </div>
        )}

        {tab === 'settings' && (
          <div className="space-y-5">
            <PayrollSettingsTab money={money} qtyFmt={qtyFmt} />
            <SupervisorConfig />
          </div>
        )}
      </main>
    </div>
  );
};

export default Payslip;
